import * as React from 'react';

import { withModal } from 'components/modal/ModalHOC';
import CampaignAssetsGalleryItem from 'modules/campaigns/components/gallery/CampaignAssetsGalleryItem';

import InspirationalMaterialsScrollArrow from './arrow/InspirationalMaterialsScrollArrow';

import { __t, applyTranslations } from 'utils/language';
import { WITH_GALLERY_MODAL_CONFIG } from 'utils/gallery';
import { prepareCampaignAssetsForGallery } from 'utils/campaignAsset';
import useKeyboard from 'hooks/use-keyboard';

import type { CampaignAsset } from 'types';

import './inspirational-materials-selected.scss';

applyTranslations(require('./inspirational-materials-selected.i18n.json'));

type Props = {
  assets: Array<CampaignAsset>,
  onDeleteAsset: (a: CampaignAsset) => void
};

const DivWithModal = withModal(<div />, WITH_GALLERY_MODAL_CONFIG);
const VISIBLE_ITEMS = 6;

const InspirationalMaterialsSelected = (
  {
    assets,
    onDeleteAsset
  }: Props
): JSX.Element => {
  const galleryRef = React.useRef<HTMLUListElement>(null);
  const [currentIndex, setIndex] = React.useState(0);
  const [currentAmountOfItems, setAmountOfItems] = React.useState(assets.length);
  const lastPossibleIndex = currentAmountOfItems - VISIBLE_ITEMS;

  useKeyboard((key) => {
    if (galleryRef.current) {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      galleryRef.current?.clientWidth; // force reflow to fix Chrome animation jittering
    }

    if (key === 'ArrowLeft' && currentIndex > 0) {
      setIndex(currentIndex - 1);
    } else if (key === 'ArrowRight' && currentIndex < lastPossibleIndex) {
      setIndex(currentIndex + 1);
    }
  });

  React.useEffect(() => {
    if (currentAmountOfItems === 0 && assets.length > 0) {
      // initial load, only update amount of items
      setAmountOfItems(assets.length);
    } else if (assets.length < currentAmountOfItems) {
      // item was removed, update amount of items
      setAmountOfItems(assets.length);

      // shift if at the end of carousel
      if (currentIndex === lastPossibleIndex) {
        const diff = currentAmountOfItems - assets.length;
        setIndex(Math.max(0, currentIndex - diff));
      }
    } else if (assets.length > currentAmountOfItems && assets.length > VISIBLE_ITEMS) {
      // when new items is added will scroll to the end to display it
      setIndex(assets.length - VISIBLE_ITEMS);
      setAmountOfItems(assets.length);
    }
  }, [assets.length]);

  const hasNoArrows = assets.length <= VISIBLE_ITEMS;
  const galleryItems = prepareCampaignAssetsForGallery(assets);

  const galleryTransform = {
    transform: `translateX(-${134 * currentIndex}px)`
  };

  return (
    <section className="inspirational-materials-selected">
      <div className="inspirational-materials-selected__title">
        {__t('inspirational_materials_selected.title')}
      </div>
      <div className="inspirational-materials-selected__gallery">
        <InspirationalMaterialsScrollArrow
          direction="prev"
          isDisabled={currentIndex === 0}
          isHidden={hasNoArrows}
          onClick={() => setIndex(currentIndex - 1)}
        />
        <div className="inspirational-materials-selected__items-container">
          <ul
            className="inspirational-materials-selected__items"
            ref={galleryRef}
            style={galleryTransform}
          >
            {
              assets.map((asset, index) => (
                <DivWithModal
                  modalContentProps={{
                    items: galleryItems,
                    currentPosition: index
                  }}
                  key={`inspirational-material-${asset.id}`}
                >
                  <CampaignAssetsGalleryItem
                    canEdit
                    width={116}
                    height={116}
                    className="inspirational-materials-selected__item"
                    asset={asset}
                    onDeleteAsset={onDeleteAsset}
                  />
                </DivWithModal>
              ))
            }
          </ul>
        </div>
        <InspirationalMaterialsScrollArrow
          direction="next"
          isDisabled={currentIndex >= lastPossibleIndex}
          isHidden={hasNoArrows}
          onClick={() => setIndex(currentIndex + 1)}
        />
      </div>
    </section>
  );
};

export default InspirationalMaterialsSelected;
